import _ from 'lodash';
import {
  Area,
  ArrayArea,
  ArrayCellPosition,
  CellPosition,
  Freeze,
  IRange,
  SelectionCell,
  SheetSelection,
} from '../../types';

export const getDefaultPosition = (): CellPosition => ({ ci: 0, ri: 0 });

export const convertPositionToArea = (position: CellPosition): Area => ({
  start: { ...position },
  end: { ...position },
});

export const getMaxGridArea = (maxColIndex: number, maxRowIndex: number) => ({
  start: { ci: 0, ri: 0 },
  end: { ci: maxColIndex, ri: maxRowIndex },
});

export const convertPositionToSheetSelection = (
  position: CellPosition,
  cell: SelectionCell
): SheetSelection => ({
  area: convertPositionToArea(position),
  cell: cell,
});

export const areEqualPositions = (p1: CellPosition, p2: CellPosition) =>
  p1.ci === p2.ci && p1.ri === p2.ri;

export const createSheetSelection = (
  area = convertPositionToArea({ ci: 0, ri: 0 }),
  cell = SelectionCell.Cell
) => ({
  area,
  cell,
});

export const getDefaultArea = (): Area => ({
  start: getDefaultPosition(),
  end: getDefaultPosition(),
});

export const isFloat = (n: number): boolean => n === +n && n !== (n | 0);
export const indexToColumnLetter = (number: number): string => {
  const num = number + 1;
  if (num < 1 || isFloat(num)) return '';

  let dividend = num;
  let name = '';
  let modulo = 0;

  while (dividend > 0) {
    modulo = (dividend - 1) % 26;
    name = String.fromCharCode('A'.charCodeAt(0) + modulo) + name;
    dividend = Math.floor((dividend - modulo) / 26);
  }

  return name;
};

export const columnLetterToIndex = (str: string) => {
  str = str.toUpperCase();
  let out = 0,
    len = str.length;
  for (let pos = 0; pos < len; pos++) {
    out += (str.charCodeAt(pos) - 64) * Math.pow(26, len - pos - 1);
  }
  return out - 1;
};

export const isCellInsideArea = (
  { ri, ci }: CellPosition,
  area: Area
): boolean => {
  const { start, end } = area;

  const isInsideRowRange = ri >= start.ri && ri <= end.ri;
  const isInsideColRange = ci >= start.ci && ci <= end.ci;

  return isInsideRowRange && isInsideColRange;
};

export const isAreaInsideArea = (innerArea: Area, outerArea: Area): boolean =>
  innerArea.start.ci >= outerArea.start.ci &&
  innerArea.start.ri >= outerArea.start.ri &&
  innerArea.end.ci <= outerArea.end.ci &&
  innerArea.end.ri <= outerArea.end.ri;

export const doAreasIntersect = (area1: Area, area2: Area): boolean =>
  area1.start.ci <= area2.end.ci &&
  area1.end.ci >= area2.start.ci &&
  area1.start.ri <= area2.end.ri &&
  area1.end.ri >= area2.start.ri;

export const getIntersectedArea = (area1: Area, area2: Area): Area | null => {
  const startCi = Math.max(area1.start.ci, area2.start.ci);
  const startRi = Math.max(area1.start.ri, area2.start.ri);
  const endCi = Math.min(area1.end.ci, area2.end.ci);
  const endRi = Math.min(area1.end.ri, area2.end.ri);

  // Check if there is a valid intersection
  // if (startCi <= endCi && startRi <= endRi) {
  if (doAreasIntersect(area1, area2)) {
    return {
      start: { ci: startCi, ri: startRi },
      end: { ci: endCi, ri: endRi },
    };
  }

  // No intersection
  return null;
};

export const reduceAreaByIntersection = (
  originalArea: Area,
  intersectedArea: Area
): Area[] => {
  let reducedAreas: Area[] = [];

  // Left non-overlapping area
  if (intersectedArea.start.ri > originalArea.start.ri) {
    reducedAreas.push({
      start: originalArea.start,
      end: {
        ci: originalArea.end.ci,
        ri: intersectedArea.start.ri - 1,
      },
    });
  }

  // Right non-overlapping area
  if (intersectedArea.end.ri < originalArea.end.ri) {
    reducedAreas.push({
      start: {
        ci: originalArea.start.ci,
        ri: intersectedArea.end.ri + 1,
      },
      end: originalArea.end,
    });
  }

  // Top non-overlapping area
  if (intersectedArea.start.ci > originalArea.start.ci) {
    reducedAreas.push({
      start: {
        ci: originalArea.start.ci,
        ri: intersectedArea.start.ri,
      },
      end: {
        ci: intersectedArea.start.ci - 1,
        ri: intersectedArea.end.ri,
      },
    });
  }

  // Bottom non-overlapping area
  if (intersectedArea.end.ci < originalArea.end.ci) {
    reducedAreas.push({
      start: {
        ci: intersectedArea.end.ci + 1,
        ri: intersectedArea.start.ri,
      },
      end: {
        ci: originalArea.end.ci,
        ri: intersectedArea.end.ri,
      },
    });
  }

  return reducedAreas;
};

export const areConsecutiveAreas = (area1: Area, area2: Area) => {
  const consecutiveCol =
    area1.end.ci + 1 === area2.start.ci || area2.end.ci + 1 === area1.start.ci;

  const consecutiveRow =
    area1.end.ri + 1 === area2.start.ri || area2.end.ri + 1 === area1.start.ri;

  if (consecutiveCol) {
    return area1.start.ri === area2.start.ri && area1.end.ri === area2.end.ri;
  }

  if (consecutiveRow) {
    return area1.start.ci === area2.start.ci && area1.end.ci === area2.end.ci;
  }

  return false;
  // return consecutiveCol && consecutiveRow
};

export const combineConsecutiveAreas = (
  areas: Area[],
  combineInnerAreas = true
): Area[] => {
  const mergedAreas: Area[] = [];

  for (let i = 0; i < areas.length; i++) {
    let merged = false;
    for (let j = 0; j < mergedAreas.length; j++) {
      if (i === j) continue;
      if (
        areConsecutiveAreas(mergedAreas[j], areas[i]) ||
        (combineInnerAreas &&
          (isAreaInsideArea(areas[i], areas[j]) ||
            isAreaInsideArea(areas[j], areas[i])))
      ) {
        mergedAreas[j] = unionAreas(mergedAreas[j], areas[i]);
        merged = true;
        break;
      }
    }

    if (!merged) {
      mergedAreas.push(areas[i]);
    }
  }
  const result =
    mergedAreas.length === areas.length
      ? mergedAreas
      : combineConsecutiveAreas(mergedAreas);

  return result;
};

export const unionAreas = (area1: Area, area2: Area): Area => ({
  start: {
    ri: Math.min(area1.start.ri, area2.start.ri),
    ci: Math.min(area1.start.ci, area2.start.ci),
  },
  end: {
    ri: Math.max(area1.end.ri, area2.end.ri),
    ci: Math.max(area1.end.ci, area2.end.ci),
  },
});

export const isSingleCell = (area: Area) =>
  area.start.ci === area.end.ci && area.start.ri === area.end.ri;

export const isSingleColumn = (area: Area) => area.start.ci === area.end.ci;

export const isEqualAreas = (area1: Area, area2: Area) =>
  area1.start.ci === area2.start.ci &&
  area1.start.ri === area2.start.ri &&
  area1.end.ci === area2.end.ci &&
  area1.end.ri === area2.end.ri;

export const mergeAreas = (area1: Area, area2: Area): Area => {
  const start = {
    ci: Math.min(area1.start.ci, area2.start.ci),
    ri: Math.min(area1.start.ri, area2.start.ri),
  };

  const end = {
    ci: Math.max(area1.end.ci, area2.end.ci),
    ri: Math.max(area1.end.ri, area2.end.ri),
  };

  return { start, end };
};

export const convertRowFreezeRangeToArea = (
  range: IRange,
  viewportArea: Area
) => ({
  start: { ci: viewportArea.start.ci, ri: range.start },
  end: { ci: viewportArea.end.ci, ri: range.end },
});

export const convertColFreezeRangeToArea = (
  range: IRange,
  viewportArea: Area
) => ({
  start: { ri: viewportArea.start.ri, ci: range.start },
  end: { ri: viewportArea.end.ri, ci: range.end },
});

export function createArrayAreaAddressFromPosition({ ri, ci }: CellPosition) {
  return `${ri},${ci}`;
}

export function createArrayAreaAddressFromArea(area: Area) {
  const isCell = isSingleCell(area);
  const { start, end } = area;
  let address = '';
  if (isCell) {
    address = createArrayAreaAddressFromPosition(start); //`${start.ri},${start.ci}`
  } else {
    address = `${start.ri},${start.ci},${end.ri},${end.ci}`;
  }

  return address;
}

export const convertArrayAreaAddressToArea = (address: string) => {
  // ``` Fastest Way To Split by comma is by using custom loop
  const parts = [];
  let currentValue = '';
  for (let i = 0; i < address.length; i++) {
    const char = address[i];
    if (char !== ',') {
      currentValue += char;
    } else {
      parts.push(parseInt(currentValue));
      currentValue = '';
    }
  }
  if (currentValue !== '') {
    parts.push(parseInt(currentValue));
  }
  // ```

  if (parts.length === 2 || parts.length === 4) {
    const [rs, cs, re, ce] = parts;
    return {
      start: {
        ri: rs,
        ci: cs,
      },
      end: {
        ri: re || rs,
        ci: ce || cs,
      },
    };
  }
  return null;
};

export function addAreasToExistingAreas(existingAreas: Area[], areas: Area[]) {
  let updatedAreas = _.cloneDeep(areas);
  // let updatedAreas = [area];

  for (const existingArea of existingAreas) {
    const newAreas: Area[] = [];

    for (const newArea of updatedAreas) {
      const intersection = getIntersectedArea(existingArea, newArea);
      if (intersection) {
        reduceAreaByIntersection(newArea, intersection).forEach((newArea) => {
          newAreas.push(newArea);
        });
      } else {
        newAreas.push(newArea);
      }
    }
    updatedAreas = newAreas;
  }

  return {
    mergedAreas: existingAreas.concat(updatedAreas),
    uniqueAreas: updatedAreas,
  };
}
